import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Bug, Lightbulb, HelpCircle, Loader2, Upload, X, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSupport, TicketType } from "@/contexts/SupportContext";
import { getRecentErrors } from "@/lib/console-buffer";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp", "image/gif"];

const baseSchema = z.object({
  title: z.string().trim().min(3, "Add a short title").max(200, "Max 200 characters"),
  description: z.string().trim().min(10, "Tell us a bit more").max(5000, "Max 5000 characters"),
  guestEmail: z.string().trim().email("Enter a valid email").max(255).optional(),
});

const TYPE_META: Record<TicketType, { icon: any; label: string; color: string }> = {
  bug: { icon: Bug, label: "Bug", color: "text-destructive" },
  feature: { icon: Lightbulb, label: "Feature idea", color: "text-amber-500" },
  question: { icon: HelpCircle, label: "Question", color: "text-blue-500" },
};

export function SubmitTicketDialog() {
  const { user } = useAuth();
  const { ticketOpen, ticketType, prefillTitle, closeTicket } = useSupport();
  const [type, setType] = useState<TicketType>(ticketType);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [severity, setSeverity] = useState<"low" | "medium" | "high" | "blocking">("medium");
  const [guestEmail, setGuestEmail] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset when dialog reopens
  useEffect(() => {
    if (!ticketOpen) return;
    setType(ticketType);
    setTitle(prefillTitle);
    setDescription("");
    setStepsToReproduce("");
    setExpected("");
    setActual("");
    setSeverity("medium");
    setGuestEmail("");
    setFile(null);
    setShowContext(false);
  }, [ticketOpen, ticketType, prefillTitle]);

  const capturedContext = useMemo(() => {
    if (!ticketOpen) return null;
    return {
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      viewport:
        typeof window !== "undefined"
          ? `${window.innerWidth}x${window.innerHeight}`
          : "",
      role: user?.user_metadata?.user_type || (user ? "authenticated" : "guest"),
      userId: user?.id || null,
      timestamp: new Date().toISOString(),
      recentErrors: getRecentErrors().slice(-15),
    };
  }, [ticketOpen, user]);

  const handleFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    if (!ALLOWED_MIME.includes(f.type)) {
      toast.error("Only PNG, JPEG, WEBP or GIF images are accepted");
      return;
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      toast.error("Screenshot must be under 5MB");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const parsed = baseSchema.safeParse({
      title,
      description,
      guestEmail: !user ? guestEmail : undefined,
    });
    if (!parsed.success) {
      const first = parsed.error.errors[0];
      toast.error(first?.message || "Please review the form");
      return;
    }
    if (!user && !guestEmail.trim()) {
      toast.error("Please add an email so we can follow up");
      return;
    }

    setSubmitting(true);
    try {
      // Upload attachment first (if any)
      let attachmentUrl: string | null = null;
      if (file) {
        const folder = user?.id || "guest";
        const ext = file.name.split(".").pop()?.toLowerCase() || "png";
        const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("support-attachments")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        attachmentUrl = path; // store path; admin reads via signed URL
      }

      const insertPayload: Record<string, unknown> = {
        type,
        title: title.trim(),
        description: description.trim(),
        context: capturedContext ?? {},
        attachment_url: attachmentUrl,
      };
      if (user) {
        insertPayload.user_id = user.id;
      } else {
        insertPayload.guest_email = guestEmail.trim();
      }
      if (type === "bug") {
        insertPayload.severity = severity;
        if (stepsToReproduce.trim()) insertPayload.steps_to_reproduce = stepsToReproduce.trim();
        if (expected.trim()) insertPayload.expected_behavior = expected.trim();
        if (actual.trim()) insertPayload.actual_behavior = actual.trim();
      }

      const { data: ticket, error } = await supabase
        .from("support_tickets")
        .insert(insertPayload as any)
        .select("id, ticket_number")
        .maybeSingle();

      if (error) throw error;

      // Fire notification email (best-effort; don't block UX)
      try {
        await supabase.functions.invoke("notify-support-ticket", {
          body: { ticket_id: ticket.id, kind: "new" },
        });
      } catch (notifyErr) {
        console.warn("Support notification failed", notifyErr);
      }

      toast.success(`Thanks - your ticket FC-${ticket.ticket_number} was submitted.`);
      closeTicket();
    } catch (err: any) {
      console.error("Submit support ticket failed", err);
      toast.error(err?.message || "Couldn't submit your ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const TypeIcon = TYPE_META[type].icon;

  return (
    <Dialog open={ticketOpen} onOpenChange={(o) => (!o && !submitting ? closeTicket() : null)}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className={cn("h-5 w-5", TYPE_META[type].color)} />
            Submit a ticket
          </DialogTitle>
          <DialogDescription>
            We read every submission. Please be specific so we can help quickly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type toggle */}
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(TYPE_META) as TicketType[]).map((t) => {
              const Icon = TYPE_META[t].icon;
              const active = type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs transition-colors",
                    active
                      ? "border-primary bg-primary/5 text-foreground font-semibold"
                      : "border-border/50 hover:border-border text-muted-foreground"
                  )}
                >
                  <Icon className={cn("h-4 w-4", active && TYPE_META[t].color)} />
                  {TYPE_META[t].label}
                </button>
              );
            })}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="ticket-title">Title</Label>
            <Input
              id="ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                type === "bug"
                  ? "e.g. Can't accept booking offer"
                  : type === "feature"
                  ? "e.g. Bulk-archive booking requests"
                  : "What would you like to know?"
              }
              maxLength={200}
              disabled={submitting}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ticket-desc">Description</Label>
            <Textarea
              id="ticket-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={5000}
              placeholder={
                type === "bug"
                  ? "Describe what happened. Screenshots help a lot."
                  : type === "feature"
                  ? "What problem would this solve? Who benefits?"
                  : "Add any context that will help us answer."
              }
              disabled={submitting}
            />
          </div>

          {/* Bug-only extras */}
          {type === "bug" && (
            <>
              <div className="space-y-1.5">
                <Label>Severity</Label>
                <Select value={severity} onValueChange={(v: any) => setSeverity(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - minor annoyance</SelectItem>
                    <SelectItem value="medium">Medium - workable but frustrating</SelectItem>
                    <SelectItem value="high">High - blocks part of my work</SelectItem>
                    <SelectItem value="blocking">Blocking - can't continue</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ticket-expected">Expected</Label>
                  <Textarea
                    id="ticket-expected"
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="What you expected"
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ticket-actual">Actual</Label>
                  <Textarea
                    id="ticket-actual"
                    value={actual}
                    onChange={(e) => setActual(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="What happened instead"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ticket-steps">Steps to reproduce (optional)</Label>
                <Textarea
                  id="ticket-steps"
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  rows={3}
                  maxLength={3000}
                  placeholder={"1. Go to…\n2. Click…\n3. See…"}
                  disabled={submitting}
                />
              </div>
            </>
          )}

          {/* Attachment */}
          <div className="space-y-1.5">
            <Label>Screenshot (optional)</Label>
            {file ? (
              <div className="flex items-center justify-between gap-3 p-2.5 border rounded-lg bg-muted/30">
                <span className="text-xs truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  disabled={submitting}
                  aria-label="Remove attachment"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                className={cn(
                  "flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer text-sm text-muted-foreground hover:border-primary/40 hover:bg-accent/30 transition-colors",
                  submitting && "opacity-50 pointer-events-none"
                )}
              >
                <Upload className="h-4 w-4" />
                <span>Drop or click to attach (PNG, JPG, max 5MB)</span>
                <input
                  type="file"
                  accept={ALLOWED_MIME.join(",")}
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0] || null)}
                />
              </label>
            )}
          </div>

          {/* Guest email */}
          {!user && (
            <div className="space-y-1.5">
              <Label htmlFor="ticket-email">Your email</Label>
              <Input
                id="ticket-email"
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="you@example.com"
                maxLength={255}
                disabled={submitting}
              />
              <p className="text-[11px] text-muted-foreground">
                We'll only use this to follow up on your report.
              </p>
            </div>
          )}

          {/* Captured context (collapsible) */}
          {capturedContext && (
            <Collapsible open={showContext} onOpenChange={setShowContext}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown
                    className={cn("h-3 w-3 transition-transform", showContext && "rotate-180")}
                  />
                  {showContext ? "Hide" : "Show"} what we'll send with your report
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <pre className="text-[10px] bg-muted/40 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                  {JSON.stringify(capturedContext, null, 2)}
                </pre>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={closeTicket} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Submit ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SubmitTicketDialog;
